export type TransitionName = 'none' | 'fade' | 'scale' | 'slide';
export type SlideDirection = 'up' | 'down' | 'left' | 'right';

export type TransitionOptions = {
  durationClass?: string; // e.g., 'duration-200' | 'duration-300' | `duration-[250ms]`
  easingClass?: string; // e.g., 'ease-out' | 'ease-in-out'
  slideDirection?: SlideDirection;
};

const DEFAULT_DURATION = 'duration-200';
const DEFAULT_EASING = 'ease-out';

function slideClasses(direction: SlideDirection) {
  switch (direction) {
    case 'up': {
      return 'data-[side=bottom]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-2';
    }
    case 'down': {
      return 'data-[side=top]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2';
    }
    case 'left': {
      return 'data-[side=right]:slide-in-from-right-2 data-[state=closed]:slide-out-to-right-2';
    }
    case 'right': {
      return 'data-[side=left]:slide-in-from-left-2 data-[state=closed]:slide-out-to-left-2';
    }
    default: {
      return '';
    }
  }
}

export function buildTransitionClasses(
  name: TransitionName,
  options: TransitionOptions = {},
): string {
  const {
    durationClass = DEFAULT_DURATION,
    easingClass = DEFAULT_EASING,
    slideDirection = 'up',
  } = options;

  if (name === 'none') {
    return '';
  }

  const base = 'data-[state=open]:animate-in data-[state=closed]:animate-out';

  if (name === 'fade') {
    return [
      base,
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      durationClass,
      easingClass,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (name === 'scale') {
    return [
      base,
      'data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
      durationClass,
      easingClass,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (name === 'slide') {
    return [
      base,
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      slideClasses(slideDirection),
      durationClass,
      easingClass,
    ]
      .filter(Boolean)
      .join(' ');
  }

  return '';
}

export const transitions = {
  none: () => '',
  fade: (options?: TransitionOptions) => buildTransitionClasses('fade', options),
  scale: (options?: TransitionOptions) => buildTransitionClasses('scale', options),
  slide: (options?: TransitionOptions) => buildTransitionClasses('slide', options),
};

export function getHorizontalSlideClasses(
  isVisible: boolean,
  slideFrom: 'left' | 'right',
  options: {
    baseClasses?: string;
    durationClass?: string;
    easingClass?: string;
  } = {},
): string {
  const {
    baseClasses = 'absolute inset-0 flex flex-col transition-transform will-change-transform',
    durationClass = 'duration-300',
    easingClass = 'ease-in-out',
  } = options;

  const visibilityClass = isVisible
    ? 'translate-x-0'
    : slideFrom === 'left'
      ? '-translate-x-full pointer-events-none overflow-hidden'
      : 'translate-x-full pointer-events-none overflow-hidden';

  return [baseClasses, durationClass, easingClass, visibilityClass].filter(Boolean).join(' ');
}

const transformMap: Record<
  'left' | 'right' | 'up' | 'down',
  Record<'0.5' | '1' | '2' | '4', string>
> = {
  left: {
    '0.5': '-translate-x-0.5',
    '1': '-translate-x-1',
    '2': '-translate-x-2',
    '4': '-translate-x-4',
  },
  right: {
    '0.5': 'translate-x-0.5',
    '1': 'translate-x-1',
    '2': 'translate-x-2',
    '4': 'translate-x-4',
  },
  up: {
    '0.5': '-translate-y-0.5',
    '1': '-translate-y-1',
    '2': '-translate-y-2',
    '4': '-translate-y-4',
  },
  down: {
    '0.5': 'translate-y-0.5',
    '1': 'translate-y-1',
    '2': 'translate-y-2',
    '4': 'translate-y-4',
  },
};

export function getHoverTransformClasses(
  direction: 'left' | 'right' | 'up' | 'down',
  distance: '0.5' | '1' | '2' | '4' = '0.5',
  options: {
    durationClass?: string;
    easingClass?: string;
    groupHover?: string; // e.g., 'group-hover/menu-item' or 'group-hover'
  } = {},
): string {
  const {
    durationClass = 'duration-200',
    easingClass = 'ease-in-out',
    groupHover = 'group-hover/menu-item',
  } = options;

  const transformClass = transformMap[direction][distance];

  return ['transition-transform', durationClass, easingClass, `${groupHover}:${transformClass}`]
    .filter(Boolean)
    .join(' ');
}
